/**
 * Dosetronic Events Table - Recent dosing events with pagination
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Paper,
  Text,
  Stack,
  Table,
  Pagination,
  Group,
  Badge,
  ActionIcon,
  Tooltip,
  Checkbox,
} from '@mantine/core';
import { IconTrash, IconPencil, IconNote } from '@tabler/icons-react';
import type { DosingEvent } from '@/types/dosing';
import { useDeleteManualDosing, useToggleDosingExclusion } from '@/hooks/useDosingEvents';
import { EditDosingModal } from '@/components/EditDosingModal';
import { DosingEventFilter, normaliseSource } from './DosingEventFilter';

// Helper to check if event is user-editable (manual entries and task-created entries)
const isManualEntry = (event: DosingEvent): boolean => {
  return event.source === 'manual' || event.source === 'task' || event.pump_number === 0;
};

// Format timestamp for display
const formatTimestamp = (timestamp: string) => {
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

// Get chemical name (server-resolved for all events)
const getChemicalDisplay = (event: DosingEvent) => {
  return event.chemical_name || 'Unknown';
};

// Truncate notes for display
const truncateNotes = (notes: string | undefined, maxLength: number = 30) => {
  if (!notes) return null;
  if (notes.length <= maxLength) return notes;
  return notes.substring(0, maxLength) + '...';
};

interface DosetronicEventsTableProps {
  events: DosingEvent[];
}

const EVENTS_PER_PAGE = 50;

export function DosetronicEventsTable({
  events,
}: DosetronicEventsTableProps) {
  const [page, setPage] = useState(1);
  const [editModalOpened, setEditModalOpened] = useState(false);
  const [editingEvent, setEditingEvent] = useState<DosingEvent | null>(null);
  const [selectedChemical, setSelectedChemical] = useState<string | null>(null);
  const [selectedSource, setSelectedSource] = useState<string | null>(null);
  const deleteMutation = useDeleteManualDosing();
  const excludeMutation = useToggleDosingExclusion();

  // Reset filter selections when the dataset fundamentally changes (e.g. date range change)
  // Use a stable key derived from event count + first/last timestamps to avoid
  // resetting on every refetch (which would clear filters on exclude toggle)
  const datasetKey = useMemo(() => {
    if (events.length === 0) return 'empty';
    const first = events[0]?.timestamp ?? '';
    const last = events[events.length - 1]?.timestamp ?? '';
    return `${events.length}-${first}-${last}`;
  }, [events]);

  useEffect(() => {
    setSelectedChemical(null);
    setSelectedSource(null);
    setPage(1);
  }, [datasetKey]);

  // Compute filtered events directly from events + selections
  const filteredEvents = useMemo(() => {
    let filtered = events;
    if (selectedChemical) {
      filtered = filtered.filter(
        (e) => (e.chemical_name || 'Unknown') === selectedChemical,
      );
    }
    if (selectedSource) {
      filtered = filtered.filter(
        (e) => normaliseSource(e) === selectedSource,
      );
    }
    return filtered;
  }, [events, selectedChemical, selectedSource]);

  // Sort the *filtered* events by timestamp (most recent first)
  const sortedEvents = useMemo(() => {
    return [...filteredEvents].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }, [filteredEvents]);

  // Paginate events
  const paginatedEvents = useMemo(() => {
    const startIndex = (page - 1) * EVENTS_PER_PAGE;
    const endIndex = startIndex + EVENTS_PER_PAGE;
    return sortedEvents.slice(startIndex, endIndex);
  }, [sortedEvents, page]);

  const totalPages = Math.ceil(sortedEvents.length / EVENTS_PER_PAGE);

  const handleChemicalChange = (chemical: string | null) => {
    setSelectedChemical(chemical);
    setSelectedSource(null);
    setPage(1);
  };

  const handleSourceChange = (source: string | null) => {
    setSelectedSource(source);
    setPage(1);
  };

  // Handle delete for manual entries
  const handleDelete = async (eventId: number) => {
    if (confirm('Delete this dosing entry?')) {
      deleteMutation.mutate(eventId);
    }
  };

  // Handle edit for manual entries
  const handleEdit = (event: DosingEvent) => {
    setEditingEvent(event);
    setEditModalOpened(true);
  };

  return (
    <>
    <Paper p="md" withBorder radius="lg">
      <Stack gap="md">
        <Group justify="space-between">
          <Text size="lg" fw={600}>
            Recent Events
          </Text>
          <Text size="sm" c="dimmed">
            {sortedEvents.length} total event{sortedEvents.length !== 1 ? 's' : ''}
          </Text>
        </Group>

        <DosingEventFilter
          events={events}
          selectedChemical={selectedChemical}
          selectedSource={selectedSource}
          onChemicalChange={handleChemicalChange}
          onSourceChange={handleSourceChange}
          filteredCount={filteredEvents.length}
        />

        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Timestamp</Table.Th>
              <Table.Th>Chemical</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Volume (mL)</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Remaining (mL)</Table.Th>
              <Table.Th>Notes</Table.Th>
              <Table.Th style={{ width: 60 }}>Excl</Table.Th>
              <Table.Th style={{ width: 80 }}></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {paginatedEvents.length > 0 ? (
              paginatedEvents.map((event, index) => (
                <Table.Tr
                  key={`${event.timestamp}-${event.pump_number}-${index}`}
                  style={{
                    backgroundColor: event.excluded ? 'var(--mantine-color-red-0)' : undefined,
                    opacity: event.excluded ? 0.6 : 1,
                  }}
                >
                  <Table.Td>
                    <Text size="sm">{formatTimestamp(event.timestamp)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      {isManualEntry(event) ? (
                        <Badge variant="light" size="sm" color="grape">
                          Manual
                        </Badge>
                      ) : (
                        <Badge variant="light" size="sm">
                          {event.pump_label || `Pump ${event.pump_number}`}
                        </Badge>
                      )}
                      <Stack gap={0}>
                        <Text size="sm" fw={500}>
                          {getChemicalDisplay(event)}
                        </Text>
                        {isManualEntry(event) && event.brand && (
                          <Text size="xs" c="dimmed">
                            {event.brand}
                          </Text>
                        )}
                      </Stack>
                    </Group>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    <Text size="sm">
                      {event.volume_ml !== undefined && event.volume_ml !== null
                        ? event.volume_ml.toFixed(1)
                        : 'N/A'}
                    </Text>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {isManualEntry(event) ? (
                      <Text size="sm" c="dimmed">—</Text>
                    ) : (
                      <Text size="sm" c="dimmed">
                        {event.remaining_ml !== undefined && event.remaining_ml !== null
                          ? event.remaining_ml.toFixed(0)
                          : 'N/A'}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {isManualEntry(event) && event.user_note ? (
                      <Tooltip label={event.user_note} multiline maw={300}>
                        <Group gap={4} style={{ cursor: 'help' }}>
                          <IconNote size={14} color="var(--mantine-color-dimmed)" />
                          <Text size="sm" c="dimmed">
                            {truncateNotes(event.user_note)}
                          </Text>
                        </Group>
                      </Tooltip>
                    ) : (
                      <Text size="sm" c="dimmed">—</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Checkbox
                      size="xs"
                      checked={event.excluded ?? false}
                      onChange={() => {
                        if (event.id) {
                          excludeMutation.mutate({
                            eventId: event.id,
                            excluded: !event.excluded,
                          });
                        }
                      }}
                      disabled={!event.id}
                      aria-label={event.excluded ? 'Include this dosing event' : 'Exclude this dosing event'}
                    />
                  </Table.Td>
                  <Table.Td>
                    {isManualEntry(event) && event.id && (
                      <Group gap={4}>
                        <Tooltip label="Edit entry">
                          <ActionIcon
                            variant="subtle"
                            color="blue"
                            size="sm"
                            onClick={() => handleEdit(event)}
                          >
                            <IconPencil size={14} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Delete entry">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            size="sm"
                            onClick={() => handleDelete(event.id!)}
                            loading={deleteMutation.isPending}
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))
            ) : (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Text c="dimmed" ta="center" py="md">
                    No dosing events found for this date range
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>

        {totalPages > 1 && (
          <Group justify="center">
            <Pagination
              value={page}
              onChange={setPage}
              total={totalPages}
              siblings={1}
              boundaries={1}
            />
          </Group>
        )}
      </Stack>
    </Paper>

    <EditDosingModal
      opened={editModalOpened}
      onClose={() => {
        setEditModalOpened(false);
        setEditingEvent(null);
      }}
      event={editingEvent}
    />
    </>
  );
}
